import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react'

import { FOCUS_RING } from './focusRing.ts'

export type TagTone = 'brand' | 'neutral' | 'info' | 'success' | 'warning' | 'danger'
export type TagVariant = 'filled' | 'stroke'

export interface TagAction extends Omit<
  ComponentPropsWithRef<'button'>,
  'aria-label' | 'children' | 'className' | 'type'
> {
  label: string
}

export interface TagProps extends Omit<ComponentPropsWithRef<'span'>, 'children' | 'onClick'> {
  children: ReactNode
  tone?: TagTone
  variant?: TagVariant
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  action?: TagAction
  leadingIconAction?: TagAction
  trailingIconAction?: TagAction
}

const TONE_CLASSES: Record<TagTone, string> = {
  brand: 'text-text-brand',
  neutral: 'text-text-weak',
  info: 'text-text-information',
  success: 'text-text-success',
  warning: 'text-text-warning',
  danger: 'text-text-error',
}

const ICON_TONE_CLASSES: Record<TagTone, string> = {
  brand: 'text-icon-brand',
  neutral: 'text-icon-neutral',
  info: 'text-icon-information',
  success: 'text-icon-success',
  warning: 'text-icon-warning',
  danger: 'text-icon-error',
}

const FILLED_CLASSES: Record<TagTone, string> = {
  brand: 'bg-fill-brand-weak',
  neutral: 'bg-fill-weak',
  info: 'bg-fill-information-weak',
  success: 'bg-fill-success-weak',
  warning: 'bg-fill-warning-weak',
  danger: 'bg-fill-error-weak',
}

const STROKE_CLASSES: Record<TagTone, string> = {
  brand: 'border border-stroke-brand-weak',
  neutral: 'border border-stroke-weak',
  info: 'border border-stroke-information-weak',
  success: 'border border-stroke-success-weak',
  warning: 'border border-stroke-warning-weak',
  danger: 'border border-stroke-error-weak',
}

function joinClasses(...classes: Array<string | undefined | false>): string {
  return classes.filter(Boolean).join(' ')
}

interface TagIconProps {
  icon: ReactNode
  // See the note in Select.tsx: exactOptionalPropertyTypes is on here.
  action: TagAction | undefined
  position: 'leading' | 'trailing'
  tone: TagTone
}

function TagIcon({ icon, action, position, tone }: TagIconProps): ReactElement {
  const iconClasses = joinClasses(
    'inline-flex size-[16px] shrink-0 items-center justify-center leading-none [&>svg]:size-[12px]',
    ICON_TONE_CLASSES[tone],
  )

  if (!action) {
    return (
      <span aria-hidden="true" data-slot={`${position}-icon`} className={iconClasses}>
        {icon}
      </span>
    )
  }

  const { label, disabled, ...buttonProps } = action

  return (
    <button
      {...buttonProps}
      type="button"
      disabled={disabled}
      aria-label={label}
      data-slot={`${position}-icon-action`}
      className={joinClasses(
        'relative z-20 inline-flex size-[16px] shrink-0 cursor-pointer items-center justify-center rounded-full leading-none transition-colors duration-150',
        FOCUS_RING,
        'hover:bg-fill-hover active:bg-fill-press',
        'disabled:cursor-default disabled:text-icon-disabled disabled:hover:bg-transparent disabled:active:bg-transparent',
        ICON_TONE_CLASSES[tone],
      )}
    >
      <span aria-hidden="true" className="inline-flex [&>svg]:size-[12px]">
        {icon}
      </span>
    </button>
  )
}

/**
 * Reusable Quave Run design-system tag matching Figma ingredient set 253:2593.
 */
export function Tag({
  children,
  tone = 'brand',
  variant = 'filled',
  leadingIcon,
  trailingIcon,
  action,
  leadingIconAction,
  trailingIconAction,
  className,
  ...rest
}: TagProps): ReactElement {
  const {
    label: actionLabel,
    disabled: actionDisabled,
    ...actionProps
  } = action ?? {
    label: '',
  }

  return (
    <span
      {...rest}
      data-tone={tone}
      data-variant={variant}
      data-interactive={action ? 'true' : undefined}
      className={joinClasses(
        'relative isolate inline-flex h-7 w-fit shrink-0 items-center gap-1 self-start whitespace-nowrap rounded-full px-2',
        'text-tP',
        TONE_CLASSES[tone],
        variant === 'filled' ? FILLED_CLASSES[tone] : STROKE_CLASSES[tone],
        action && !actionDisabled && 'cursor-pointer',
        className,
      )}
    >
      {action ? (
        <button
          {...actionProps}
          type="button"
          disabled={actionDisabled}
          aria-label={actionLabel}
          data-slot="tag-action"
          className={joinClasses(
            'absolute inset-0 z-0 rounded-full transition-colors duration-150',
            FOCUS_RING,
            'hover:bg-fill-hover/50 active:bg-fill-press/50',
            'disabled:cursor-default disabled:hover:bg-transparent disabled:active:bg-transparent',
          )}
        />
      ) : null}

      {leadingIcon ? (
        <TagIcon icon={leadingIcon} action={leadingIconAction} position="leading" tone={tone} />
      ) : null}

      <span className={joinClasses('relative z-10', action && 'pointer-events-none')}>
        {children}
      </span>

      {trailingIcon ? (
        <TagIcon icon={trailingIcon} action={trailingIconAction} position="trailing" tone={tone} />
      ) : null}
    </span>
  )
}
